import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { exitDemo } from '@/lib/demo';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const interceptorId = useRef(null);

  // Clear all auth state locally (used both on manual logout and on 401)
  const clearAuth = () => {
    exitDemo();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  // On mount and whenever the token changes, validate against backend.
  // NEVER trust localStorage 'user' blindly — always ask the backend who the token belongs to.
  useEffect(() => {
    let cancelled = false;
    async function loadUser() {
      // CRITICAL: If returning from Google OAuth callback, skip the /me check.
      // AuthCallback will exchange the session_id and establish the session first.
      if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
        setLoading(false);
        return;
      }
      if (!token) {
        setUser(null);
        localStorage.removeItem('user');
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled) return;
        setUser(res.data);
        // Overwrite cached user with the one the backend confirms belongs to this token
        localStorage.setItem('user', JSON.stringify(res.data));
      } catch (err) {
        if (cancelled) return;
        // Token invalid / expired → clear everything and force /login
        clearAuth();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadUser();
    return () => { cancelled = true; };
  }, [token]);

  // Global 401 interceptor: if any authenticated call fails with 401, force logout.
  useEffect(() => {
    if (interceptorId.current !== null) {
      axios.interceptors.response.eject(interceptorId.current);
    }
    interceptorId.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401 && token) {
          clearAuth();
        }
        if (error?.response?.status === 403 && error?.response?.data?.detail === 'account_disabled' && token) {
          clearAuth();
          window.alert('Tu cuenta ha sido desactivada por el administrador.');
        }
        return Promise.reject(error);
      }
    );
    return () => {
      if (interceptorId.current !== null) {
        axios.interceptors.response.eject(interceptorId.current);
      }
    };
  }, [token]);

  const login = async (email, password) => {
    // Defensive: wipe any previous session before setting the new one
    clearAuth();
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: newUser } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    setUser(newUser);
    setToken(newToken); // triggers /users/me revalidation
    return newUser;
  };

  const register = async (userData) => {
    clearAuth();
    const response = await axios.post(`${API}/auth/register`, userData);
    const { token: newToken, user: newUser } = response.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    setUser(newUser);
    setToken(newToken);
    return newUser;
  };

  const logout = () => {
    clearAuth();
  };

  const refreshUser = async () => {
    if (!token) return null;
    try {
      const res = await axios.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
      return res.data;
    } catch (err) {
      return null;
    }
  };

  const getAuthHeader = () => (token ? `Bearer ${token}` : '');

  const hydrateFromToken = async (newToken, newUser) => {
    const wasDemo = localStorage.getItem('rs_demo') === '1';
    clearAuth();
    if (wasDemo) { localStorage.setItem('rs_demo', '1'); axios.defaults.headers.common['X-Demo-Mode'] = '1'; }
    localStorage.setItem('token', newToken);
    if (newUser) {
      localStorage.setItem('user', JSON.stringify(newUser));
      setUser(newUser);
    }
    setToken(newToken);
    return newUser;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, getAuthHeader, refreshUser, hydrateFromToken }}>
      {children}
    </AuthContext.Provider>
  );
};
